import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Info,
  Loader2,
} from 'lucide-react';
import {
  AuthHeading,
  AuthPrimaryButton,
  AuthShell,
} from '../components/AuthShell';
import {
  applyEmailActionCode,
  confirmPasswordResetWithCode,
  isCurrentPassword,
  verifyPasswordReset,
} from '../gcp/gcpAuth';
import { announceEmailVerified } from '../gcp/emailVerifiedSignal';
import { AuthErrorBanner } from '../components/fieldError';
import { maskEmail } from '../components/mask';
import {
  PasswordChecklist,
  passwordMeetsPolicy,
} from '@/shared/components/PasswordPolicy';

/**
 * Branded handler for the links Identity Platform emails out.
 *
 * Firebase mails links pointing at ITS OWN hosted handler by default — the
 * grey "Your email has been verified" page with a Google favicon and no sign
 * of BIASafe. Setting the project's action URL to this route means the link
 * lands here instead, and we redeem the code ourselves via the client SDK.
 *
 * Firebase always appends the same query params, whatever the mode:
 *   ?mode=verifyEmail|resetPassword|recoverEmail&oobCode=…&continueUrl=…&lang=…
 *
 * Every mode is handled, because once the action URL is overridden Firebase
 * routes ALL of them here — an unhandled mode would be a dead end for the user
 * rather than a fallback to Google's page.
 */

const FONT = 'var(--font-brand)';
const PURPLE = '#7F22FE';

type Phase = 'working' | 'success' | 'error' | 'needPassword';

/**
 * Firebase surfaces a small, stable set of failure codes here. Map them to
 * copy that says what happened AND what to do — an expired link is the common
 * case and is recoverable, so it must not read like a hard error.
 */
function messageForError(err: unknown, mode: string): string {
  const code =
    typeof err === 'object' && err !== null && 'code' in err
      ? String(err.code)
      : '';
  switch (code) {
    case 'auth/invalid-action-code':
    case 'auth/expired-action-code':
      return mode === 'resetPassword'
        ? // The next step is the link right underneath, which says it in its
          // own words. "Request a new one to continue" was the button read
          // aloud a second time.
          'This password reset link has expired or was already used.'
        : 'This confirmation link has expired or was already used. If you have already confirmed your email, just sign in.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact your administrator.';
    case 'auth/user-not-found':
      return 'We could not find an account for this link.';
    case 'auth/weak-password':
      return 'That password does not meet the requirements below.';
    default:
      return 'Something went wrong completing this request. Please try again.';
  }
}

/**
 * FINALIZED UI — reviewed against the Figma export and signed off.
 *
 * Do not redesign this screen. If a change to how it LOOKS seems obviously
 * right, read the comment next to the thing first: most of what reads as odd
 * here is a decision with its reason written beside it. See
 * features/Auth/README.md before editing, and change the shared component in
 * components/AuthShell.tsx or components/fieldError.tsx rather than this file,
 * so the flow does not drift apart again.
 *
 * This applies to AI agents as much as to people.
 */
/**
 * The path to continue to, or null if the URL is not one of ours.
 *
 * `continueUrl` arrives in a query string, so it is attacker-supplied in the
 * shape that matters: anyone can mint a link to our action handler carrying any
 * destination they like. Redirecting to it unchecked is an open redirect, and
 * this page is reached by people who have just proved control of an email
 * address — exactly the audience a convincing hop to a lookalike domain is
 * aimed at.
 *
 * So only the PATH is ever used, and only when the origin matches ours.
 * Anything else falls back to the login page.
 */
export function samePathOrNull(raw: string): string | null {
  if (!raw) return null;
  try {
    const parsed = new URL(raw, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function AuthActionPage() {
  const navigate = useNavigate();
  // These params are minted by Firebase, not by our route definitions, so they
  // are absent from the generated search-param union — hence the widening cast.
  const search = useSearch({ strict: false }) as unknown as {
    mode?: string;
    oobCode?: string;
    continueUrl?: string;
  };
  const mode = search.mode ?? '';
  const oobCode = search.oobCode ?? '';
  // Where the link said to go afterwards. Firebase carries this through
  // untouched; this page is the only thing that can honour it.
  const continueUrl = search.continueUrl ?? '';

  const [phase, setPhase] = useState<Phase>('working');
  const [error, setError] = useState<string | null>(null);
  const [resetEmail, setResetEmail] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // One toggle for both fields on purpose: when you are typing the same secret
  // twice, revealing one and not the other is not the thing anyone wants.
  const [showPassword, setShowPassword] = useState(false);
  const [pwTouched, setPwTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  // React 18 StrictMode mounts effects twice in dev. Action codes are
  // single-use, so a second redeem would fail with invalid-action-code and
  // show an error for a link that actually worked.
  const redeemed = useRef(false);

  useEffect(() => {
    if (redeemed.current) return;
    redeemed.current = true;

    if (!oobCode) {
      setError('This link is missing its confirmation code.');
      setPhase('error');
      return;
    }

    const run = async () => {
      try {
        if (mode === 'resetPassword') {
          setResetEmail(await verifyPasswordReset(oobCode));
          setPhase('needPassword');
          return;
        }
        if (mode === 'verifyEmail' || mode === 'recoverEmail') {
          await applyEmailActionCode(oobCode);
          // Let a sign-up tab that is still waiting carry on by itself, so
          // the user is not sent to retype the password they just chose. It
          // carries no data; see emailVerifiedSignal.
          if (mode === 'verifyEmail') announceEmailVerified();
          setPhase('success');
          return;
        }
        setError('This link is not one we recognise.');
        setPhase('error');
      } catch (err) {
        setError(messageForError(err, mode));
        setPhase('error');
      }
    };
    void run();
  }, [mode, oobCode]);

  const submitNewPassword = useCallback(async () => {
    setPwTouched(true);
    setConfirmTouched(true);
    if (!passwordMeetsPolicy(password)) return;
    if (confirmPassword !== password) return;
    setSubmitting(true);
    setError(null);
    try {
      // Refuse the password they already have, BEFORE spending the code.
      //
      // A reset is often asked for because the old password is suspected of
      // being known by somebody else, and setting the same one again produces a
      // flow that looks like it worked and changed nothing. The check has to
      // come first: `confirmPasswordResetWithCode` consumes the oobCode, so
      // after it there is no second attempt to offer.
      //
      // Only reachable when the link told us the address -- resetEmail comes
      // from verifyPasswordReset. Without it the check is skipped rather than
      // guessed at.
      if (resetEmail && (await isCurrentPassword(resetEmail, password))) {
        setError(
          'That is your current password. Choose one you have not used here before.',
        );
        return;
      }
      await confirmPasswordResetWithCode(oobCode, password);
      setPhase('success');
    } catch (err) {
      setError(messageForError(err, 'resetPassword'));
    } finally {
      setSubmitting(false);
    }
  }, [oobCode, password, confirmPassword, resetEmail]);

  // "Continue" honours the link's own continueUrl, falling back to the advisor
  // login.
  //
  // It used to be hardcoded to /login, and for a CLIENT that was the wrong
  // product entirely: a portal invitation sets `continueUrl=/portal/login`,
  // and ignoring it sent every client who had just chosen their password to
  // the advisor sign-in page instead. Firebase mints the parameter on every
  // link it sends; nothing else in the app is in a position to act on it.
  const goToLogin = () => {
    const target = samePathOrNull(continueUrl);
    if (target) {
      // A full assignment rather than a router navigate: the destination may
      // sit in the other route tree (the portal), which has its own gate and
      // its own layout, and a client-side transition would carry this page's
      // context into it.
      window.location.assign(target);
      return;
    }
    void navigate({ to: '/login', replace: true });
  };

  const pwValid = passwordMeetsPolicy(password);
  const confirmMismatch = confirmPassword.length > 0 && confirmPassword !== password;

  return (
    <AuthShell dataName="AuthAction">
      <div className="w-full" style={{ fontFamily: FONT }}>
            {phase === 'working' && (
              <div className="text-center">
                <Loader2
                  className="mx-auto h-8 w-8 animate-spin"
                  style={{ color: PURPLE }}
                />
                <p className="mt-4 text-[15px] text-[#717182]">
                  Confirming your request…
                </p>
              </div>
            )}

            {phase === 'success' && (
              <div className="text-center">
                <CheckCircle2
                  className="mx-auto h-12 w-12"
                  style={{ color: '#16a34a' }}
                />
                <AuthHeading
                  title={
                    mode === 'resetPassword'
                      ? 'Password updated'
                      : mode === 'recoverEmail'
                        ? 'Email address restored'
                        : 'Email confirmed'
                  }
                >
                  {mode === 'resetPassword'
                    ? 'Your new password is active. Sign in to continue.'
                    : mode === 'recoverEmail'
                      ? 'Your account has been returned to its previous email address. We recommend resetting your password as well.'
                      : 'Your account is active. Sign in to set up your workspace.'}
                </AuthHeading>
                <div className="mt-7">
                  <AuthPrimaryButton onClick={goToLogin}>
                    Continue to sign in
                  </AuthPrimaryButton>
                </div>
              </div>
            )}

            {phase === 'error' && (
              <div className="text-center">
                <AlertCircle className="mx-auto h-12 w-12 text-[#ff4d4f]" />
                <AuthHeading title="Link no longer valid" />
                <p
                  className="mt-3 text-[15px] leading-[1.6] text-[#717182]"
                  role="alert"
                >
                  {error}
                </p>
                <div className="mt-7">
                  <AuthPrimaryButton onClick={goToLogin}>
                    Go to sign in
                  </AuthPrimaryButton>
                </div>
                {mode === 'resetPassword' && (
                  /* The same secondary link as "Forgot Password" on the
                     sign-in page, down to the hex. Purple is the BRAND accent
                     -- it marks the shield on the two-factor gate and the
                     Recommended pill -- so spending it on an ordinary text link
                     made this one read as the important thing on a screen whose
                     important thing is the black button above it. Two levels of
                     emphasis, in the wrong order. */
                  <button
                    type="button"
                    onClick={() => navigate({ to: '/forgot-password' })}
                    className="mt-4 text-[15px] leading-[20px] text-[#3d3d3d] hover:text-[#121212] transition-colors cursor-pointer hover:underline"
                  >
                    Request a new reset link
                  </button>
                )}
              </div>
            )}

            {phase === 'needPassword' && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitNewPassword();
                }}
              >
                {/* The address goes in the heading block, masked.
                    
                    Naming the account matters -- it is what tells someone they
                    have opened a reset link for the wrong one -- but the whole
                    address does not have to be on screen to do that. This page
                    is reached from a link in an inbox, on whatever machine
                    happens to be open, and it is the one screen in the flow
                    that will sit there while somebody walks away from it. Three
                    characters and the domain identify the account to its owner
                    and to nobody else. Same masking as the two-factor gate. */}
                <AuthHeading title="Choose a new password">
                  {resetEmail
                    ? `Reset password for ${maskEmail(resetEmail)}`
                    : undefined}
                </AuthHeading>

                {/* Same field as signup, not a near-miss of it.
                    
                    This screen was built against shadcn-ish primitives while
                    signup was translated from the Figma export, so the two drew
                    the same control differently: `rounded-lg` against
                    `rounded-md`, a flat `#e5e5e5` resting border against
                    `#121212/20`, no shadow, and -- most visible of all -- a
                    PURPLE focus ring where every other field in the flow turns
                    black. An invalid field also went uncoloured here while
                    signup turns its border red. */}
                <div
                  className={`mt-6 relative rounded-md shrink-0 w-full border shadow-xs ${
                    pwTouched && !pwValid
                      ? 'border-[#ff4d4f] bg-transparent'
                      : 'border-[#121212]/20 bg-transparent focus-within:border-[#121212]'
                  }`}
                >
                  <div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
                    <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[12px] py-[0px] relative w-full">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onBlur={() => setPwTouched(true)}
                        autoComplete="new-password"
                        autoFocus
                        placeholder="New password"
                        aria-label="New password"
                        style={{ fontFamily: FONT }}
                        className="basis-0 flex flex-col grow justify-center leading-[0] min-h-px min-w-0 not-italic relative shrink-0 text-[#121212] h-9 text-base md:text-sm placeholder:text-muted-foreground bg-transparent border-none outline-none"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={
                          showPassword ? 'Hide password' : 'Show password'
                        }
                        className="relative shrink-0 size-[20px] cursor-pointer hover:opacity-70 transition-opacity"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5 text-[#5B5B5B]" />
                        ) : (
                          <Eye className="w-5 h-5 text-[#5B5B5B]" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* The message and the checklist belong to the field above
                    them, and to each other -- one block, message first, exactly
                    as signup stacks them.
                    
                    They were split: the checklist sat after BOTH inputs and the
                    red line after the checklist, so the complaint about a
                    password was two elements away from the box that caused it,
                    with the confirm field in between. Reading order matters more
                    than it looks here: "does not meet the requirements" has to
                    come before the requirements, or it is describing something
                    the eye has already passed. */}
                {password.length > 0 && (
                  <div className="mt-3 flex flex-col gap-[8px] w-full">
                    {pwTouched && !pwValid && (
                      <div className="flex items-center gap-[6px]">
                        <Info className="w-[14px] h-[14px] text-[#ff4d4f] shrink-0" />
                        <p
                          className="text-[#ff4d4f] text-[13px] font-medium"
                          style={{ fontFamily: FONT }}
                          role="alert"
                        >
                          Password does not meet requirements
                        </p>
                      </div>
                    )}
                    <PasswordChecklist value={password} fontFamily={FONT} />
                  </div>
                )}

                {/* Confirm, because this is the one place a typo is
                    unrecoverable in the same breath. Everywhere else a wrong
                    password just fails to sign you in and you try again; here
                    the mistyped value BECOMES the password, the reset code is
                    spent, and the only way back is another email. Signup asks
                    twice for a password the user can still change; this screen
                    had more reason to and asked once. */}
                <div
                  className={`mt-4 relative rounded-md shrink-0 w-full border shadow-xs ${
                    confirmTouched && confirmMismatch
                      ? 'border-[#ff4d4f] bg-transparent'
                      : 'border-[#121212]/20 bg-transparent focus-within:border-[#121212]'
                  }`}
                >
                  <div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
                    <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[12px] py-[0px] relative w-full">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onBlur={() => setConfirmTouched(true)}
                        autoComplete="new-password"
                        placeholder="Confirm new password"
                        aria-label="Confirm new password"
                        aria-invalid={confirmMismatch || undefined}
                        style={{ fontFamily: FONT }}
                        className="basis-0 flex flex-col grow justify-center leading-[0] min-h-px min-w-0 not-italic relative shrink-0 text-[#121212] h-9 text-base md:text-sm placeholder:text-muted-foreground bg-transparent border-none outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Under the box it is about, like every other message here. */}
                {confirmTouched && confirmMismatch && (
                  <div className="mt-2 flex items-center gap-[6px]">
                    <Info className="w-[14px] h-[14px] text-[#ff4d4f] shrink-0" />
                    <p
                      className="text-[#ff4d4f] text-[13px] font-medium"
                      style={{ fontFamily: FONT }}
                      role="alert"
                    >
                      Both passwords must match
                    </p>
                  </div>
                )}

                {/* The form-level refusal -- an expired link, or the password
                    they already have. Not about either box specifically, so it
                    sits with the button that produced it. */}
                {error && (
                  <div className="mt-4">
                    <AuthErrorBanner>{error}</AuthErrorBanner>
                  </div>
                )}

                <div className="mt-6">
                  <AuthPrimaryButton type="submit" disabled={submitting}>
                    {submitting ? 'Updating…' : 'Update password'}
                  </AuthPrimaryButton>
                </div>
              </form>
        )}
      </div>
    </AuthShell>
  );
}
